/**
 * Hooks for public share links (Feature 10).
 *
 * `useTravelShareLinks(tripId)` — list + create + revoke for a single trip.
 *   Used from the Share dialog in the planner header.
 *
 * `useAllTravelShareLinks()` — cross-trip audit list for the Sharing
 *   settings tab so the owner can kill every active link from one place.
 *
 * `useSharedTravelTrip(shareId)` — public read used by TravelSharedTripPage.
 *   Doesn't require auth; attaches nothing to the request besides the path.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { travelApi } from '../api';
import type {
  TravelShareLinkResponse,
  TravelShareLinkWithTripResponse,
  TravelSharedTripResponse,
} from '../api';
import { queryKeys } from '@/lib/query-keys';
import { useTravelFeature } from './useTravelUserProfile';

export function useTravelShareLinks(tripId: string | undefined) {
  const queryClient = useQueryClient();
  const feature = useTravelFeature('share_links');

  const listKey = tripId
    ? queryKeys.travel.shareLinks(tripId)
    : queryKeys.travel.disabled('shareLinks');

  const listQuery = useQuery({
    enabled: !!tripId,
    queryKey: listKey,
    queryFn: () => travelApi.listTravelShareLinks(tripId!),
  });

  const invalidate = () => {
    if (!tripId) return;
    void queryClient.invalidateQueries({ queryKey: queryKeys.travel.shareLinks(tripId) });
    void queryClient.invalidateQueries({ queryKey: queryKeys.travel.shareLinksAll() });
  };

  const createMut = useMutation({
    mutationFn: (input: { expiresAt?: string | null } = {}) =>
      travelApi.createTravelShareLink(tripId!, input),
    onSuccess: invalidate,
  });

  const revokeMut = useMutation({
    mutationFn: (shareId: string) => travelApi.revokeTravelShareLink(tripId!, shareId),
    onSuccess: invalidate,
  });

  return {
    featureEnabled: feature.enabled,
    shareLinks: (listQuery.data?.shareLinks ?? []) as TravelShareLinkResponse[],
    loading: listQuery.isLoading,
    error: listQuery.error instanceof Error ? listQuery.error.message : null,
    refresh: () => listQuery.refetch(),
    createShareLink: (input?: { expiresAt?: string | null }) => createMut.mutateAsync(input ?? {}),
    revokeShareLink: (shareId: string) => revokeMut.mutateAsync(shareId),
    isCreating: createMut.isPending,
    isRevoking: revokeMut.isPending,
  };
}

export function useAllTravelShareLinks() {
  const queryClient = useQueryClient();
  const feature = useTravelFeature('share_links');

  const listQuery = useQuery({
    queryKey: queryKeys.travel.shareLinksAll(),
    queryFn: () => travelApi.listAllTravelShareLinks(),
  });

  const revokeMut = useMutation({
    mutationFn: (vars: { tripId: string; shareId: string }) =>
      travelApi.revokeTravelShareLink(vars.tripId, vars.shareId),
    onSuccess: (_res, vars) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.travel.shareLinksAll() });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.travel.shareLinks(vars.tripId),
      });
    },
  });

  return {
    featureEnabled: feature.enabled,
    shareLinks: (listQuery.data?.shareLinks ?? []) as TravelShareLinkWithTripResponse[],
    loading: listQuery.isLoading,
    refresh: () => listQuery.refetch(),
    revokeShareLink: (tripId: string, shareId: string) =>
      revokeMut.mutateAsync({ tripId, shareId }),
    isRevoking: revokeMut.isPending,
  };
}

/** Unauthenticated shared-trip read. Used by the public SharedTripPage. */
export function useSharedTravelTrip(shareId: string | undefined) {
  const query = useQuery<TravelSharedTripResponse>({
    enabled: !!shareId,
    queryKey: shareId
      ? queryKeys.travel.sharedTrip(shareId)
      : queryKeys.travel.disabled('sharedTrip'),
    queryFn: () => travelApi.getSharedTravelTrip(shareId!),
    retry: false,
  });
  return query;
}
